<template>
    <div
        :class="[
            'l-select',
            'form-group',
            'label-floating',
            { 'is-focused': isFocused },
            { 'remove-padding': disabled || !showHelpBlock },
            { 'has-error': error },
            wrapperClass,
        ]"
        data-test-id="l-select"
    >
        <label v-show="label" class="control-label" :for="id">
            {{ label }}
        </label>
        <div :class="{ 'input-group': $slots.addon }">
            <div class="select-wrapper">
                <select
                    :id="id"
                    :class="['form-control', 'clickable', 'tw-truncate', inputClass]"
                    :value="value"
                    :disabled="disabled"
                    :readonly="readonly"
                    @focus="onFocus"
                    @blur="onBlur"
                    @change="onChange"
                    @click="onClick"
                >
                    <option
                        v-for="opt in options"
                        :key="opt.key || opt.value"
                        :value="opt.value"
                        :selected="opt.selected"
                        :disabled="opt.disabled"
                    >
                        {{ opt.text }}
                    </option>
                </select>
                <i class="select-arrow"></i>
            </div>
            <template v-if="$slots.addon">
                <div class="input-group-addon ">
                    <slot name="addon"></slot>
                </div>
            </template>
        </div>

        <!-- Error -->
        <p v-show="error" class="help-block" data-test-error="">
            {{ $t("skills.modal.error-required") }}
        </p>
        <!--/Error -->
    </div>
</template>

<script>
import generateDefaultId from "@/utils/generate-default-id";

export default {
    props: {
        label: {
            type: String,
            default: "",
        },
        id: {
            type: String,
            default() {
                return generateDefaultId();
            },
        },
        options: {
            type: Array,
            required: true,
            validator: opts =>
                opts.every(
                    opt => Object.keys(opt).length >= 2 && opt.value != null && opt.text != null,
                ),
        },
        value: {
            type: [String, Boolean],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        showHelpBlock: {
            type: Boolean,
            default: false,
        },
        wrapperClass: {
            type: String,
            default: "",
        },
        inputClass: {
            type: String,
            default: "",
        },
        onClick: {
            type: Function,
            default: () => {},
        },
        validator: {
            type: Function, // Returns `true` if value is valid
            default: () => true,
        },
        errorMessage: {
            type: String,
            default() {
                return this.$t("error.input.generic");
            },
        },
    },
    data() {
        return {
            isFocused: false,
            error: false,
        };
    },
    methods: {
        onChange(e) {
            this.$emit("change", e);
            this.$emit("input", e.target.value);
            this.$emit("update:value", e.target.value);
        },
        onFocus() {
            this.isFocused = true;
            this.error = false;

            this.$emit("focus");
        },
        onBlur() {
            this.isFocused = false;

            this.validate();

            this.$emit("blur");
        },

        /**
         * Validate the input value
         */
        validate() {
            const isValid = this.validator(this.value);

            this.error = !isValid;

            return isValid;
        },
    },
};
</script>

<style scoped>
.form-group {
    /* Extra padding-bottom is required to ensure there is enough room for the error text */
    padding-bottom: 20px;
    margin-top: 0px;
}

.form-group.remove-padding {
    /* Remove extra padding when input disabled */
    padding-bottom: 0;
}

.form-group .form-control {
    padding-right: 24px;
}

.form-group.has-error .form-control {
    outline: none;
    background-size: 100% 2px, 100% 1px;
    background-image: linear-gradient(#f44336, #f44336), linear-gradient(#d2d2d2, #d2d2d2);
    transition-duration: 0.3s;
}
.form-group.has-error .help-block,
.form-group.show-help .help-block {
    display: block;
    margin-top: -8px;
}

/** Some defaults for usage in tailwind layouts */
.tw.form-group {
    line-height: 1;
}

.tw.form-group,
.tw.form-group .control-label {
    margin: 0;
    padding: 0;
}

.tw.form-group .form-control {
    margin: 0;
}

.tw label.control-label {
    top: 0 !important;
    position: unset !important;
    line-height: 1 !important;
    margin-bottom: 4px !important;
}

.tw .help-block {
    margin-top: 0 !important;
    position: unset !important;
    line-height: 20px !important;
}
</style>
